/**
 * Admin Users service layer.
 * Plain async functions — no React, no hooks.
 */
import { supabase } from '@/core/supabase/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Role {
  id: string;
  role_code: string;
  label: string;
  description: string | null;
  mfa_required: boolean;
}

export interface CfaOption {
  id: string;
  nome: string;
}

export interface AppModule {
  code: string;
  label: string;
  module_type: string;
  display_order: number;
}

export interface CfaAssignment {
  cfaId: string;
  cfaRole: 'operator' | 'supervisor' | 'admin';
}

export interface CreateUserParams {
  email: string;
  password: string;
  nome: string;
  cognome: string;
  roleId: string;
  /** Primary CFA (passed to edge function for the first assignment) */
  cfaId: string;
  cfaRole: 'operator' | 'supervisor' | 'admin';
}

export interface CreateUserResult {
  success: boolean;
  userId: string;
  warning?: string;
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchRoles(): Promise<Role[]> {
  const { data, error } = await supabase
    .from('roles')
    .select('id, role_code, label, description, mfa_required')
    .order('label');
  if (error) throw error;
  return (data ?? []) as Role[];
}

export async function fetchMfaRequiredRoles(): Promise<string[]> {
  const { data, error } = await supabase
    .from('roles')
    .select('role_code')
    .eq('mfa_required', true);
  if (error) throw error;
  return (data ?? []).map(r => r.role_code);
}

export async function updateRoleMfaRequired(
  roleCode: string,
  mfaRequired: boolean
): Promise<void> {
  const { error } = await supabase
    .from('roles')
    .update({ mfa_required: mfaRequired })
    .eq('role_code', roleCode);
  if (error) throw error;
}

export async function fetchActiveCfas(): Promise<CfaOption[]> {
  const { data, error } = await supabase
    .from('cfa')
    .select('id, nome')
    .eq('status', 'active')
    .order('nome');
  if (error) throw error;
  return (data ?? []) as CfaOption[];
}

export async function fetchAppModules(): Promise<AppModule[]> {
  const { data, error } = await (supabase.from as any)('app_modules')
    .select('code, label, module_type, display_order')
    .eq('is_active', true)
    .order('display_order');
  if (error) throw error;
  return (data ?? []) as AppModule[];
}

export async function fetchRoleModuleDefaults(roleCode: string): Promise<Set<string>> {
  const { data, error } = await (supabase.from as any)('role_module_defaults')
    .select('module_code')
    .eq('role_code', roleCode);
  if (error) throw error;
  return new Set((data ?? []).map((r: any) => r.module_code as string));
}

// ---------------------------------------------------------------------------
// User creation
// ---------------------------------------------------------------------------

/**
 * Creates a user via the create-admin-user edge function.
 * The edge function handles: auth user, profile, user_roles, first user_cfa_assignment.
 */
export async function createAdminUser(params: CreateUserParams): Promise<CreateUserResult> {
  const { data, error } = await supabase.functions.invoke('create-admin-user', {
    body: {
      email: params.email,
      password: params.password,
      nome: params.nome,
      cognome: params.cognome,
      roleId: params.roleId,
      cfaId: params.cfaId,
      cfaRole: params.cfaRole,
    },
  });

  if (error) throw error;
  if (!data?.success) throw new Error(data?.error ?? "Errore durante la creazione dell'utente");

  return {
    success: true,
    userId: data.user.id,
    warning: data.warning,
  };
}

/**
 * Assigns a user to an additional CFA.
 * Call this for every CFA beyond the first (which the edge function already handles).
 */
export async function assignUserToCfa(
  userId: string,
  cfaId: string,
  cfaRole: 'operator' | 'supervisor' | 'admin'
): Promise<void> {
  const { error } = await supabase
    .from('user_cfa_assignments')
    .insert({ user_id: userId, cfa_id: cfaId, role: cfaRole });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Module overrides
// ---------------------------------------------------------------------------

/**
 * Saves a module override for a newly created user.
 * accessType 'grant' = force-enable; 'revoke' = force-disable.
 */
export async function setModuleOverride(
  userId: string,
  moduleCode: string,
  accessType: 'grant' | 'revoke'
): Promise<void> {
  const { error } = await (supabase.rpc as any)('set_module_override', {
    p_target_user_id: userId,
    p_module_code: moduleCode,
    p_access_type: accessType,
  });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Kiosk PIN
// ---------------------------------------------------------------------------

export interface CreateKioskOperatorParams {
  userId: string;
  nome: string;
  cognome: string;
  pin: string;
  cfaId: string;
}

export interface KioskOperatorResult {
  success: boolean;
  error?: string;
}

/**
 * Creates a kiosk operator record linked to the user for a given CFA.
 */
export async function createKioskOperator(
  params: CreateKioskOperatorParams
): Promise<KioskOperatorResult> {
  const { data, error } = await supabase.rpc('manage_kiosk_operator', {
    p_action: 'create',
    p_nome: params.nome,
    p_cognome: params.cognome,
    p_pin: params.pin,
    p_cfa_id: params.cfaId,
    p_user_id: params.userId,
  });
  if (error) throw error;
  return data as KioskOperatorResult;
}
